import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { CustomOption } from 'components/MultiSelect/CustomOption'
import { Select, SelectOption } from 'components/Select'
import { differenceInDays } from 'date-fns'
import { FiltersT } from 'modules/EpidDashboard'
import diseaseMapping from 'modules/EpidDashboard/apis/diseaseMapping'
import { formatDate } from 'modules/EpidDashboard/formatDate'
import { getCountryName } from 'modules/EpidDashboard/getCountryName'
import { parseDate } from 'modules/EpidDashboard/helpers'
import { useEffect, useMemo, useState } from 'react'
import PeakBox from './PeakBox'
import { PeakDetails } from './PeakDetails'
import useTracking from 'tracking/useTracking'

type PeakData = {
  date: string
  value: number
  diseaseName: string
  numOfTests: number
  name: string
}

const LeftSection = ({
  filters,
  peaksData,
}: {
  filters: FiltersT
  peaksData: PeakData[]
}) => {
  const [tracking] = useTracking()

  // State for reference disease and peak selection
  const [referenceDisease, setReferenceDisease] =
    useState<SelectOption<string> | null>()

  // Get unique diseases for reference disease dropdown
  const diseaseOptions = useMemo(() => {
    const uniqueDiseases = Array.from(
      new Set(peaksData.map((x) => x.diseaseName))
    )
    return [
      // Always show selected option on the top
      ...(referenceDisease
        ? [{ value: referenceDisease?.value, label: referenceDisease?.label }]
        : []),
      ...uniqueDiseases
        .filter((x) => x !== referenceDisease?.value)
        .map((disease) => ({
          label: disease,
          value: disease,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ]
  }, [peaksData, referenceDisease])

  useEffect(() => {
    if (
      !referenceDisease ||
      !diseaseOptions.find((x) => x.value === referenceDisease.value)
    ) {
      setReferenceDisease(
        diseaseOptions.find((x) => x.value === diseaseMapping.Influenza) ??
          diseaseOptions?.[0] ??
          null
      )
    }
  }, [diseaseOptions, referenceDisease])

  // Get peaks for the selected reference disease
  const referencePeakOptions = useMemo(() => {
    if (!referenceDisease) return []

    return peaksData
      .filter((peak) => peak.diseaseName === referenceDisease.value)
      .map((peak) => ({
        label: `${formatDate(parseDate(peak.date))} (${parseFloat(peak.value.toString()).toFixed(2)}%)`,
        value: peak.date,
      }))
  }, [referenceDisease, peaksData])

  const [referencePeak, setReferencePeak] =
    useState<SelectOption<string> | null>(
      referencePeakOptions.length > 0 ? referencePeakOptions[0] : null
    )

  // Update reference peak when reference disease changes
  useMemo(() => {
    if (referencePeakOptions.length > 0) {
      setReferencePeak(referencePeakOptions[0])
    } else {
      setReferencePeak(null)
    }
  }, [referencePeakOptions])

  // Calculate relative days for all peaks based on reference peak
  const peaksWithRelativeDays = useMemo(() => {
    if (!referencePeak) return []

    const referenceDate = parseDate(referencePeak.value)

    return peaksData
      .map((peak) => {
        const peakDate = parseDate(peak.date)
        const relativeDays = differenceInDays(peakDate, referenceDate)
        return {
          ...peak,
          relativeDays,
          isReference:
            peak.date === referencePeak.value &&
            peak.diseaseName === referenceDisease?.value,
        }
      })
      .sort((a, b) => a.relativeDays - b.relativeDays)
  }, [peaksData, referencePeak, referenceDisease])
  return (
    <Flex flexDir={'column'} gap='0.5rem' pb={4}>
      <Stack>
        <Stack direction='row' justifyContent='space-between'>
          <Stack gap={0} mb={1}>
            <Text fontSize='lg' fontWeight='bold'>
              Comparative Peak Timings in {getCountryName(filters.country)}{' '}
              relative to {referenceDisease?.label || 'Reference Disease'}
            </Text>
            <PeakDetails />
          </Stack>

          {/* Reference Disease and Peak Selectors */}
          <Stack direction='row' spacing={4}>
            <Stack align='start' spacing={1}>
              <Text fontSize='sm' fontWeight='medium'>
                Select Reference Disease
              </Text>
              <Box minW='200px'>
                <Select
                  value={referenceDisease}
                  onChange={(x: any) => {
                    tracking.peakReferenceDiseaseChanged({ disease: x })
                    setReferenceDisease(x)
                  }}
                  options={diseaseOptions}
                  placeholder='Select disease...'
                  isSearchable={false}
                  CustomOption={CustomOption}
                  isLoading={diseaseOptions.length === 0}
                />
              </Box>
            </Stack>

            <Stack align='start' spacing={1}>
              <Text fontSize='sm' fontWeight='medium'>
                Select Reference Peak
              </Text>
              <Box minW='250px'>
                <Select
                  value={referencePeak}
                  onChange={setReferencePeak}
                  options={referencePeakOptions}
                  placeholder='Select peak...'
                  isSearchable={false}
                  isDisabled={!referenceDisease}
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>

        {peaksData.length === 0 && (
          <Text fontSize='sm'>No peaks on the selected filters.</Text>
        )}

        {peaksWithRelativeDays.length > 0 && referencePeak && (
          <Box>
            {/* 3-Row Layout: Top (Before), Middle (Timeline), Bottom (After) */}
            <Stack spacing={0} minH='200px' position='relative'>
              {/* Top Row: Before Reference Peaks */}
              <Flex
                justify='flex-start'
                wrap='wrap'
                gap={4}
                minH='60px'
                mb='5px'
              >
                {peaksWithRelativeDays
                  .filter((peak) => peak.relativeDays <= 0)
                  .sort((a, b) => a.relativeDays - b.relativeDays) // Sort ascending (earliest first)
                  .map((peak) => (
                    <Box
                      key={`${peak.diseaseName}-${peak.date}`}
                      position='relative'
                    >
                      <Box
                        position='absolute'
                        top='100%'
                        left='50%'
                        transform='translateX(-50%)'
                        width='2px'
                        height='10px'
                        bg='gray.400'
                        zIndex={1}
                      />
                      <PeakBox peak={peak} layout='before' />
                    </Box>
                  ))}
              </Flex>

              <Flex
                align='center'
                justify='center'
                position='relative'
                h='3px'
                my='0'
              >
                <Box w='100%' h='3px' bg='gray.400' />
              </Flex>

              {/* Bottom Row: After Reference Peaks */}
              <Flex justify='flex-end' wrap='wrap' gap={4} minH='60px' mt='5px'>
                {peaksWithRelativeDays
                  .filter((peak) => peak.relativeDays > 0)
                  .sort((a, b) => a.relativeDays - b.relativeDays) // Sort ascending (earliest first)
                  .map((peak) => (
                    <Box
                      key={`${peak.diseaseName}-${peak.date}`}
                      position='relative'
                    >
                      <Box
                        position='absolute'
                        bottom='100%'
                        left='50%'
                        transform='translateX(-50%)'
                        width='2px'
                        height='10px'
                        bg='gray.400'
                        zIndex={1}
                      />
                      <PeakBox peak={peak} layout='after' />
                    </Box>
                  ))}
              </Flex>
            </Stack>
          </Box>
        )}
      </Stack>
    </Flex>
  )
}

export default LeftSection
